package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.Page
import dev.lingvo.shared.Plan
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.MarketingDocPage
import kotlinx.browser.document
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * The refund and cancellation policy.
 *
 * It exists partly because Paddle requires a linked refund policy before it will verify a domain for
 * checkout, and partly because the terms only ever said "we do not refund partial months" in a
 * subordinate clause of the billing section — which is where a refund policy goes to not be read.
 *
 * The stance is deliberately plainer than the industry norm: cancel yourself at any time, keep the
 * period you paid for, and ask for a refund if something went wrong. A policy that reserves every
 * right and grants none reads as a warning, and the customers most put off by it are the careful
 * ones worth having.
 *
 * **Consumer guarantees are named rather than disclaimed.** Lingvo is operated from Australia, and
 * Australian Consumer Law rights cannot be excluded by a term of service. Pretending otherwise is
 * both unenforceable and a bad look for a page whose whole job is to be trusted.
 */
@Page("/refunds")
@Composable
fun RefundsPage() {
    LaunchedEffect(Unit) {
        document.title = tr("refunds.refunds_lingvo", "Refunds — Lingvo")
    }
    MarketingDocPage(
        eyebrow = "Refund policy",
        title = t("refunds.cancel_any_time_ask_if_something", "Cancel any time. Ask if something went wrong."),
        lede = "Lingvo is a monthly subscription with a free tier, so you can evaluate the whole " +
            "product before paying anything. This page says what happens when you stop paying, and " +
            "when we give money back.",
        entries = refundEntries(),
        footnote = "Last updated 11 August 2026. Lingvo is operated from Australia. Paddle.com " +
            "Market Ltd is the merchant of record for every paid plan, so your invoice, your card " +
            "statement and any refund are handled by Paddle on our instruction. Questions: " +
            "support@lingvo.dev.",
    )
}

private fun refundEntries(): List<DocEntry> = listOf(
    DocEntry("try-first", tr("refunds.try_it_before_you_pay", "Try it before you pay")) {
        DocsProse(
            "The Free plan needs no card and does not expire. It includes one project, one owner " +
                "or admin seat, ${Plan.FREE.creditsPerSeat} AI translation credits a month, and " +
                "unlimited keys, languages and words. Unlimited translator and viewer seats are " +
                "free on every plan, including this one.",
        )
        DocsProse(
            tr("refunds.we_would_rather_you_found_the", "We would rather you found the limits of the free plan than paid for a month to " +
                "discover Lingvo does not fit. Nothing on the paid plans changes how the product " +
                "works — they raise limits."),
        )
    },

    DocEntry("cancelling", tr("refunds.cancelling", "Cancelling")) {
        // The path, and who may walk it. See the same correction in `Terms.kt`: "Plan and usage" is
        // a project settings tab, not a workspace one, so this sentence sent people to a word that
        // is not in the Settings index. And `CancelSubscriptionPage` is owner-only — an admin who
        // read "cancel yourself at any time" and could not was the worse half of the defect, on the
        // page a customer opens when they have already decided to leave.
        DocsList(
            listOf(
                "Cancel at any time from Settings → Billing → Cancel. No email, no retention " +
                    "call, no form.",
                "An owner has to be the one to do it — the subscription belongs to whoever bought " +
                    "it. Ask an owner to cancel, or to make you one.",
                "Your plan keeps working until the end of the period you have already paid for. " +
                    "Cancelling on day 2 of a month does not switch anything off on day 2.",
                "At the end of that period the workspace moves to the Free plan.",
                "Nothing is deleted. Your keys, translations, glossary and history stay, and you " +
                    "can export everything at any time with the CLI or the export button — before " +
                    "or after cancelling.",
            ),
        )
    },

    DocEntry("refunds", tr("refunds.refunds", "Refunds")) {
        DocsProse(
            tr("refunds.subscriptions_are_billed_monthly_in_advance", "Subscriptions are billed monthly in advance. We do not automatically refund the " +
                "unused part of a month, because you keep access for all of it."),
        )
        DocsProse(
            tr("refunds.we_do_refund_in_full_or", "We do refund, in full or in part, when the charge was not what you intended or not " +
                "what you got:"),
        )
        DocsList(
            listOf(
                "Something was broken. If Lingvo did not do what this site says it does and we " +
                    "could not fix it for you, ask and we will refund the period affected.",
                "You were charged after cancelling, or charged twice, or charged on an account you " +
                    "did not mean to renew.",
                "You upgraded by mistake and have not used the larger plan. Tell us in the same " +
                    "billing period.",
                "A seat was added by accident. Removing an owner or admin credits the difference " +
                    "against your next invoice automatically — you do not need to ask.",
            ),
        )
        DocsProse(
            tr("refunds.email_support_lingvo_dev_from_the", "Email support@lingvo.dev from the address on the account, or reply to any invoice. " +
                "There is no form and no deadline dressed up as a policy: if the charge was wrong, " +
                "the age of it is not the point. We answer within two business days."),
        )
    },

    DocEntry("credits", tr("refunds.ai_translation_credits", "AI translation credits")) {
        DocsList(
            listOf(
                "Credits are included with a plan rather than sold separately, so there is nothing " +
                    "to refund when they go unused.",
                "They reset each calendar month and do not roll over.",
                "Running out never blocks the product: translating by hand, importing, exporting, " +
                    "QA and the CLI all keep working. It pauses machine translation only.",
                "A project using its own provider key spends no credits at all, because we are not " +
                    "paying for those calls.",
            ),
        )
    },

    DocEntry("consumer-rights", tr("refunds.your_rights_under_the_law", "Your rights under the law")) {
        DocsProse(
            tr("refunds.nothing_on_this_page_limits_rights", "Nothing on this page limits rights you have that cannot be limited. Lingvo is " +
                "operated from Australia, and the consumer guarantees under Australian Consumer " +
                "Law apply where they apply — a term of service cannot exclude them, and this one " +
                "does not try to."),
        )
        DocsProse(
            tr("refunds.if_you_are_buying_from_the", "If you are buying from the EU or the UK, your statutory rights are likewise " +
                "unaffected. Paddle.com Market Ltd is the merchant of record and the seller of " +
                "record for your purchase, and their own terms apply alongside these."),
        )
    },
)
